import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../lib/auth'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { formatDateTime, titleCase } from '../../lib/format'
import type { ApprovalRequest } from '../../types'
import { Alert, Badge, Button, Card, CardBody, CardHeader, Skeleton, StatusBadge, Textarea } from '../../components/ui'

/* Faithful port of anansi/app/templates/admin/approval_detail.html + its
 * view admin/routes.py:approval_detail() and the approve/reject/cancel
 * routes. Reuses the same GET/POST /api/v1/ops/approvals/<id>* endpoints
 * ops/Approvals.tsx uses for its inline approve/reject - this page adds the
 * payload dump and the self-request "cancel instead" affordance the ops
 * list view has no room for. */

interface ApprovalDetailResponse { ok: boolean; item: ApprovalRequest }

function fieldLabel(key: string): string {
  return titleCase(key.replace(/_/g, ' '))
}

function fieldValue(value: unknown) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') {
    return <pre style={{ margin: 0, fontSize: 'var(--text-xs)', whiteSpace: 'pre-wrap' }}>{JSON.stringify(value, null, 2)}</pre>
  }
  return String(value)
}

export default function AdminApprovalDetail() {
  const { requestId } = useParams<{ requestId: string }>()
  const { user } = useAuth()
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [notes, setNotes] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'approvals', 'detail', requestId],
    queryFn: () => api.get<ApprovalDetailResponse>(`/ops/approvals/${requestId}`),
    enabled: Boolean(requestId),
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'approvals'] })
  }
  const approve = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/ops/approvals/${requestId}/approve`, { notes }),
    onSuccess: invalidate,
  })
  const reject = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/ops/approvals/${requestId}/reject`, { notes }),
    onSuccess: invalidate,
  })
  const cancel = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/ops/approvals/${requestId}/cancel`),
    onSuccess: invalidate,
  })

  if (!requestId) return <Navigate to="/approvals" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/approvals">Back to queue</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={160} /></div>

  const req = data.item
  const isOwn = req.requested_by === user?.uid
  const mutationError = approve.error ?? reject.error ?? cancel.error
  const payloadEntries = Object.entries(req.payload ?? {})

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{req.summary || req.kind_label || titleCase(req.kind)}</h1>
          <p className="page-head__sub">
            {req.kind_label || titleCase(req.kind)}
            {req.is_private && <span style={{ marginLeft: 8 }}><Badge tone="danger" dot>PRIVATE</Badge></span>}
          </p>
        </div>
        <StatusBadge status={req.status} />
      </div>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardBody>
          <div className="grid grid--2">
            <Row label="Submitted by" value={req.requested_by_name || (req.requested_by ?? '—')} />
            <Row label="Submitted at" value={formatDateTime(req.requested_at)} />
            {req.request_notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Row label="Submitter notes" value={req.request_notes} />
              </div>
            )}
            {req.reviewed_by && (
              <>
                <Row label="Reviewed by" value={req.reviewed_by_name || req.reviewed_by} />
                <Row label="Reviewed at" value={formatDateTime(req.reviewed_at)} />
              </>
            )}
            {req.review_notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Row label="Reviewer notes" value={req.review_notes} />
              </div>
            )}
            {req.result_entity_id && <Row label="Created entity" value={req.result_entity_id} />}
          </div>
        </CardBody>
      </Card>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Proposed record" />
        <CardBody flush>
          {payloadEntries.length === 0 ? (
            <p className="page-head__sub" style={{ margin: 'var(--sp-4)' }}>No details recorded.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {payloadEntries.map(([key, value]) => (
                    <tr key={key}>
                      <td style={{ color: 'var(--text-muted)', width: '30%' }}>{fieldLabel(key)}</td>
                      <td>{fieldValue(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {req.status === 'pending' && (
        <Card>
          <CardHeader title="Decision" />
          <CardBody>
            {mutationError && (
              <div style={{ marginBottom: 'var(--sp-3)' }}>
                <Alert tone="danger">{mutationError instanceof ApiError ? mutationError.message : 'That action failed.'}</Alert>
              </div>
            )}
            {isOwn ? (
              <>
                <Alert tone="warning">
                  You submitted this request — a different reviewer must approve or reject it.
                  You can still cancel it.
                </Alert>
                <div style={{ marginTop: 'var(--sp-3)' }}>
                  <Button
                    variant="danger" loading={cancel.isPending}
                    onClick={async () => {
                      if (await confirm({ title: 'Cancel this request?', destructive: true })) cancel.mutate()
                    }}
                  >
                    Cancel request
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Textarea
                  label="Notes (optional)"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <div className="row" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-3)' }}>
                  <Button
                    loading={approve.isPending}
                    onClick={async () => {
                      if (await confirm({ title: 'Approve and apply this request?' })) approve.mutate()
                    }}
                  >
                    Approve &amp; apply
                  </Button>
                  <Button
                    variant="danger" loading={reject.isPending}
                    onClick={async () => {
                      if (await confirm({ title: 'Reject this request?', destructive: true })) reject.mutate()
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <Link to="/approvals">← Back to queue</Link>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 550 }}>{value}</div>
    </div>
  )
}
